import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { NotificationContainer } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { getUserToken } from 'src/common/helpers/StorageUtils';
import { getUserProfile, loginActions } from 'src/views/public/login/actions';
import Utils from './common/helpers/Utils';
import HaccpPathConstants from './haccp/constants/PathConstants';
import HaccpContent from './haccp/route/Router';
import './style/style.scss';

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

// Containers
const TheLayout = React.lazy(() => import('./layout/TheLayout'));

// Login Page
const LoginPage = React.lazy(() => import('./views/public/login'));
const SSOLoginPage = React.lazy(() => import('./views/public/login/SSOLogin'));
// Forgot Password Page
const ForgotPassword = React.lazy(() =>
  import('./views/public/forgot-password')
);
// Reset Password Page
const ResetPassword = React.lazy(() => import('./views/public/reset-password'));
// Register Page
const Register = React.lazy(() => import('./views/public/register'));

// Heatmap measurement sharing
// const HeatmapMeasurementSharing = React.lazy(() =>
//   import('./views/public/heatmap-measurement-sharing')
// );

// Check whether logged in or not, if loggein than redirected to dashboard
const AuthRoute = ({ component: Component, userInfo, path, ...rest }) => {
  const defaultPagePath = useMemo(
    () => (userInfo ? Utils.getDefaultPagePath(userInfo?.role) : '/login'),
    [userInfo]
  );

  const unauthPaths = [
    '/ssoauth',
    '/forgot-password',
    '/reset_password',
    '/register',
    // '/heatmap-measurement/sharing',
  ];

  return (
    <Route
      {...rest}
      render={(props) => {
        if (
          !userInfo ||
          defaultPagePath === path ||
          (defaultPagePath === '/login' && unauthPaths.includes(path)) // ingnore redirect if return url is in unauth paths
        ) {
          return <Component {...props} />;
        } else {
          return <Redirect to={defaultPagePath} />;
        }
      }}
    />
  );
};

const App = () => {
  const dispatch = useDispatch();

  const { idToken, userInfo } = useSelector((state) => state.User);

  useEffect(() => {
    const channel = new BroadcastChannel('AUTH_CHANNEL');
    channel.addEventListener('message', (e) => {
      const messageData = e.data;
      if (messageData) {
        const actionType = messageData.actionType;
        switch (actionType) {
          case loginActions.LOGIN_SUCCESS:
            const token = getUserToken();
            if (!token) window.location.reload();
            break;
          case loginActions.LOGOUT_SUCCESS:
          case loginActions.TOKEN_EXPIRED_SUCCESS:
            window.location.reload();
            break;
          default:
            break;
        }
      }
    });
  }, []);

  useEffect(() => {
    if (idToken) {
      dispatch(getUserProfile());
    }
  }, [dispatch, idToken]);

  return (
    <BrowserRouter>
      <React.Suspense fallback={loading}>
        <Switch>
          <AuthRoute
            exact
            path="/login"
            name="Login Page"
            userInfo={userInfo}
            component={LoginPage}
          />
          <Route
            exact
            path="/ssoauth"
            name="Login Page"
            render={(props) => <SSOLoginPage {...props} />}
          />
          <AuthRoute
            exact
            path="/forgot-password"
            name="Forgot Password"
            userInfo={userInfo}
            component={ForgotPassword}
          />
          <AuthRoute
            exact
            path="/reset_password"
            name="Reset Password"
            userInfo={userInfo}
            component={ResetPassword}
          />
          <AuthRoute
            exact
            path="/register"
            name="Register"
            userInfo={userInfo}
            component={Register}
          />
          {/* <AuthRoute
            exact
            path="/heatmap-measurement/sharing"
            name="Heatmap measurement sharing"
            component={HeatmapMeasurementSharing}
          /> */}
          <Route
            path={HaccpPathConstants.Home}
            render={() => (userInfo ? <HaccpContent /> : <Redirect to="/" />)}
          />
          <Route
            path="/"
            name="Home"
            render={(props) => <TheLayout {...props} />}
          />
        </Switch>
        <NotificationContainer />
      </React.Suspense>
    </BrowserRouter>
  );
};

export default App;

AuthRoute.propTypes = {
  auth: PropTypes.string,
  component: PropTypes.elementType,
};
